import Address from '@/Model/Address';
import NavigationService from '@/Management/NavigationService';
import { IAssociationNavigator, IListViewParameter, INavigationParameterProvider } from '#/CoreFrameInterfaces';
import type DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import ComponentManager from '@/Management/ComponentManager';

/**
 * Bietet Methoden, die die Bedienung des NavigationService erleichtern.
 */
export default class NavigationHelper {

    private static associationNavigator: IAssociationNavigator; 
    private static navigationParameterProvider: INavigationParameterProvider;

    private static getAssociationNavigator(): IAssociationNavigator {
        if (NavigationHelper.associationNavigator == null) {
            const AssociationNavigator = ComponentManager.getUtility<IAssociationNavigator>('AssociationNavigator');
            NavigationHelper.associationNavigator = new AssociationNavigator();
        }

        return NavigationHelper.associationNavigator;
    }

    private static getNavigationParameterProvider(): INavigationParameterProvider {
        if (NavigationHelper.navigationParameterProvider == null) {
            const NavigationParameterProvider = ComponentManager.getUtility<INavigationParameterProvider>('NavigationParameterProvider');
            NavigationHelper.navigationParameterProvider = new NavigationParameterProvider();
        }

        return NavigationHelper.navigationParameterProvider;
    }

    public static setAssociationNavigator(_associationNavigator: IAssociationNavigator) {
        NavigationHelper.associationNavigator = _associationNavigator;
    }

    /**
     * Schnittstelle, um assoziative Listen anzunavigieren. 
     */
    public static navigateAssociation(typeName: string, selectedPDO: string, optionalParams: any, openInNewWindow?: boolean): void {
        NavigationHelper.getAssociationNavigator().navigateAssociation(typeName, selectedPDO, optionalParams, openInNewWindow);
    }

    public static addListViewParameters(addr: Address, params: IListViewParameter) {
        const provider = NavigationHelper.getNavigationParameterProvider();
        provider.addListViewParameters(addr, params);
    }

    public static openListViewById(viewPDO_ID: number, optionalParams?: IListViewParameter, openInNewWindow: boolean = false) {
        if (viewPDO_ID <= 0) return;

        let addr = new Address();
        addr.setParameter('ViewId', viewPDO_ID);
        this.openListViewAddress(addr, optionalParams, openInNewWindow);
    };

    public static openListViewByType(typeName: string, optionalParams?: IListViewParameter, openInNewWindow: boolean = false) {
        if (String.isNullOrEmpty(typeName)) {
            return;
        }

        NavigationHelper.openListViewByName(typeName + '_LV', optionalParams, openInNewWindow);
    }

    public static openListViewByTypeFromDetailView(currentDetailViewContext: DetailViewContext, typeName: string, optionalParams: IListViewParameter, openInNewWindow: boolean = false) {
        if (openInNewWindow) {
            NavigationHelper.openListViewByType(typeName, optionalParams, openInNewWindow);
        }
        else {
            NavigationHelper.confirmLeaveDetailViewContext(currentDetailViewContext, () => {
                NavigationHelper.openListViewByType(typeName, optionalParams, openInNewWindow);
            });
        }
    }

    public static openListViewByName(listViewName: string, optionalParams?: IListViewParameter, openInNewWindow: boolean = false) {
        if (String.isNullOrEmpty(listViewName)) {
            return;
        }
        let addr = new Address();
        addr.setParameter('ViewName', listViewName);
        this.openListViewAddress(addr, optionalParams, openInNewWindow);
    }

    public static openListViewByNameFromDetailView(currentDetailViewContext: DetailViewContext, listViewName: string, optionalParams: IListViewParameter, openInNewWindow: boolean = false): void {
        if (openInNewWindow) {
            NavigationHelper.openListViewByName(listViewName, optionalParams, openInNewWindow);
        }
        else {
            NavigationHelper.confirmLeaveDetailViewContext(currentDetailViewContext, () => {
                NavigationHelper.openListViewByName(listViewName, optionalParams, openInNewWindow);
            });
        }
    }

    private static openListViewAddress(address: Address, optionalParams: IListViewParameter = {}, openInNewWindow: boolean) {
        address.setName('ListView');
        NavigationHelper.addListViewParameters(address, optionalParams);
        NavigationService.getInstance().navigateToAddress(address, null, null, null, openInNewWindow);
    }

    public static addDetailViewParameters(addr: Address, params: any) {
        const provider = NavigationHelper.getNavigationParameterProvider();
        provider.addDetailViewParameters(addr, params);
    }

    public static openDetailView(action, typeName: string, optionalParams?, openInNewWindow: boolean = false): void {
        const addr = new Address();
        addr.setName('DetailView');
        addr.setParameters({ Action: action, TypeName: typeName });
        NavigationHelper.addDetailViewParameters(addr, optionalParams);
        NavigationService.getInstance().navigateToAddress(addr, null, null, null, openInNewWindow);
    }

    public static openDetailViewFromDetailView(currentDetailViewContext: DetailViewContext, action: string, typeName: string, optionalParams: any, openInNewWindow: boolean = false): void {
        if (openInNewWindow) {
            NavigationHelper.openDetailView(action, typeName, optionalParams, openInNewWindow);
        }
        else {
            NavigationHelper.confirmLeaveDetailViewContext(currentDetailViewContext, () => {
                NavigationHelper.openDetailView(action, typeName, optionalParams, openInNewWindow);
            });
        }
    }

    public static openDetailViewAndConnect(action: any, typeName: string, toConnectPdoId: string, toConnectTypeName: string, contextRelation: string, contextParameters?: { [key: string]: string }): void {
        return NavigationHelper.openDetailView(action, typeName, {
            pdoId: -1,
            contextObject: toConnectPdoId,
            contextTypeName: toConnectTypeName,
            contextRelation: contextRelation,
            contextParameters: contextParameters
        });
    }

    public static addExtensionViewParameters(addr: Address, params: any) {
        const provider = NavigationHelper.getNavigationParameterProvider();
        provider.addExtensionViewParameters(addr, params);
    }

    public static openExtensionView(viewExtensionFactory: string, title: string, customParams?: any, openInNewWindow: boolean = false) {
        let addr = new Address();
        addr.setName('ExtensionView');
        addr.setParameters({ Factory: viewExtensionFactory, Title: title });
        NavigationHelper.addExtensionViewParameters(addr, customParams);
        if (openInNewWindow)
            NavigationService.getInstance().navigateToAddress(addr, null, null, null, openInNewWindow);
        else
            NavigationService.getInstance().navigateToAddress(addr);
    }

    public static openWebPage(url: string, target?) {
        if (String.isNullOrEmpty(target)) {
            target = '_self';
        }

        window.open(url, target);
    }

    public static openSearch(query?: string, pageSize?: number, page?: number, openinNewWindow?: boolean) {
        const addr = new Address('Search');
        if (!String.isNullOrEmpty(query)) {
            addr.setParameter('Query', query);
        }
        else {
            addr.setParameter('Query', '');
        }

        if (pageSize != null && pageSize > 0) {
            addr.setParameter('PageSize', pageSize);
        }
        if (page != null && page >= 0) {
            addr.setParameter('Page', page);
        }

        const service = NavigationService.getInstance();
        addr.onNavigated(service.notifyNavigationReset.bind(service));

        if(openinNewWindow)
            NavigationService.getInstance().navigateToAddress(addr, null, null, null, openinNewWindow);
        else
            NavigationService.getInstance().navigateToAddress(addr);
    }

    private static confirmLeaveDetailViewContext(context: DetailViewContext, leaveCallback: () => void = () => { }, stayCallback: () => void = () => { }) {
        context.viewModel.confirmCreateContext((success) => {
            if (success)
                leaveCallback();
            else
                stayCallback();
        });
    }
}